/**
 * What a person set at the release before following „Kunde zuordnen" — the
 * ticks of the hints and gaps and the typed note — kept for this one
 * calculation in `sessionStorage` and given back once, when the release
 * stands again (review final, 6). Following the link to the record's
 * customer dialog used to lose them without a word.
 *
 * Kept, not handed to a new tab: the way stays one tab, „Zurück" works, and
 * the calculation is read again on the way back anyway. Per tab and per
 * calculation; gone once read, and gone with the tab. Nothing leaves the
 * browser.
 */
export interface ApprovalDraft {
  seen: string[];
  byHand: number[];
  note: string;
}

const KEY = (calcId: string) => `k60:freigabe:${calcId}`;

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    // A browser that refuses storage still gets a working release — only
    // the ticks are set again.
    return null;
  }
}

/** Kept for `calcId` — on the way to the customer dialog. */
export function keepApprovalDraft(calcId: string, draft: ApprovalDraft): void {
  try {
    storage()?.setItem(KEY(calcId), JSON.stringify(draft));
  } catch {
    // Full or refused: the person sets the ticks again, as before.
  }
}

/**
 * What was kept for `calcId`, or `null` — a pure read, safe in a state
 * initializer that StrictMode runs twice. `forgetApprovalDraft` ends it.
 */
export function readApprovalDraft(calcId: string): ApprovalDraft | null {
  let raw: string | null = null;
  try {
    raw = storage()?.getItem(KEY(calcId)) ?? null;
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return null;
    const { seen, byHand, note } = value as Record<string, unknown>;
    if (!Array.isArray(seen) || !seen.every((each) => typeof each === "string")) return null;
    if (!Array.isArray(byHand) || !byHand.every((each) => typeof each === "number")) return null;
    if (typeof note !== "string") return null;
    return { seen, byHand, note };
  } catch {
    return null;
  }
}

/** Given back once: after the release has taken it up, it is gone. */
export function forgetApprovalDraft(calcId: string): void {
  try {
    storage()?.removeItem(KEY(calcId));
  } catch {
    // Nothing to forget where nothing could be kept.
  }
}
